import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { pickerPage } from "../../lib/paging";
import { Button, Cluster, Input, Stack, Tooltip } from "@salesapp/ui";
import { crmApi, type Product } from "../../lib/crmApi";
import { useCrmHref } from "../../lib/customerUrl";
import { FIELD_HINTS } from "../../help/fieldHints";
import { useLocale } from "../../shell/LocaleProvider";

/** Which agreement line priced a product on this deal, shown beside the line and linked to its agreement. */
export interface AppliedAgreementLine {
  line_code?: string | null;
  agreement_id?: string | null;
  agreement_number?: string | null;
}

export interface PickedProductLine {
  product_id: string;
  quantity: number;
}

type ProductPickerProps =
  | {
      orgId: string;
      value: PickedProductLine[];
      onChange: (lines: PickedProductLine[]) => void;
      /** Keep line objects even when the list is still empty. */
      quantities?: boolean;
      label?: string;
      hint?: string;
      disabled?: boolean;
      applied?: Record<string, AppliedAgreementLine>;
      onTotalChange?: (total: number | null) => void;
    }
  | {
      orgId: string;
      value: string[];
      onChange: (ids: string[]) => void;
      quantities?: boolean;
      label?: string;
      hint?: string;
      disabled?: boolean;
      applied?: Record<string, AppliedAgreementLine>;
      onTotalChange?: (total: number | null) => void;
    };

function productLabel(product: Product, formatMoney: (value: number | null | undefined, currency?: string | null) => string) {
  const base = product.sku ? `${product.name} · ${product.sku}` : product.name;
  const uom = product.unit_of_measure ? ` / ${product.unit_of_measure}` : "";
  if (product.unit_price == null) return `${base}${uom}`;
  return `${base} · ${formatMoney(product.unit_price, product.currency)}${uom}`;
}

export function ProductPicker({
  orgId,
  value,
  onChange,
  label = "Products",
  hint = "Optional. Search for a product, specify quantity, then add it to the deal.",
  disabled = false,
  applied,
  onTotalChange,
  quantities,
}: ProductPickerProps) {
  const { formatMoney } = useLocale();
  const crmHref = useCrmHref();
  const money = (amount: number | null | undefined, currency?: string | null) => formatMoney(amount, currency, true);
  const [products, setProducts] = useState<Product[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [pendingId, setPendingId] = useState("");
  const [pendingQty, setPendingQty] = useState(1);
  const lineMode = quantities === true || (quantities !== false && value.some((item) => typeof item !== "string"));
  const lines: PickedProductLine[] = useMemo(
    () => value.map((item) => (typeof item === "string" ? { product_id: item, quantity: 1 } : item)),
    [value],
  );
  function totalFor(next: PickedProductLine[]): number | null {
    let total = 0;
    let priced = false;
    for (const line of next) {
      const product = products.find((item) => item.id === line.product_id);
      if (!product || product.unit_price == null) continue;
      total += product.unit_price * (line.quantity || 1);
      priced = true;
    }
    return priced ? total : null;
  }

  const emit = (next: PickedProductLine[]) => {
    if (lineMode) {
      (onChange as (lines: PickedProductLine[]) => void)(next);
    } else {
      (onChange as (ids: string[]) => void)(next.map((item) => item.product_id));
    }
    onTotalChange?.(totalFor(next));
  };

  useEffect(() => {
    void crmApi
      .listProducts(orgId, undefined, pickerPage())
      .then(setProducts)
      .catch((err) => setError(err instanceof Error ? err.message : "Products could not be loaded."));
  }, [orgId]);

  const available = useMemo(() => {
    const term = query.trim().toLowerCase();
    return products.filter((product) => {
      if (lines.some((line) => line.product_id === product.id)) return false;
      if (!term) return true;
      const sku = product.sku?.toLowerCase() ?? "";
      return product.name.toLowerCase().includes(term) || sku.includes(term);
    });
  }, [products, query, lines]);

  useEffect(() => {
    if (available.length === 1) {
      setPendingId(available[0].id);
      return;
    }
    if (pendingId && !available.some((product) => product.id === pendingId)) {
      setPendingId("");
    }
  }, [available, pendingId]);

  function addPending() {
    const id = pendingId || (available.length === 1 ? available[0].id : "");
    if (!id || lines.some((line) => line.product_id === id)) return;
    emit([...lines, { product_id: id, quantity: pendingQty || 1 }]);
    setQuery("");
    setPendingId("");
    setPendingQty(1);
  }

  function remove(id: string) {
    emit(lines.filter((item) => item.product_id !== id));
  }

  function setQuantity(id: string, quantity: number) {
    emit(lines.map((item) => (item.product_id === id ? { ...item, quantity: quantity || 1 } : item)));
  }

  const selectedTotal = useMemo(() => {
    let total = 0;
    let priced = false;
    let currency: string | null | undefined;
    for (const line of lines) {
      const product = products.find((item) => item.id === line.product_id);
      if (!product || product.unit_price == null) continue;
      total += product.unit_price * (line.quantity || 1);
      priced = true;
      currency ??= product.currency;
    }
    return priced ? { total, currency } : null;
  }, [products, lines]);

  return (
    <fieldset className="crm-product-picker">
      <legend className="crm-field-label">
        <Tooltip text={FIELD_HINTS.opportunity_products} label={`About ${label}`}>
          <span>{label}</span>
        </Tooltip>
      </legend>
      {hint ? <p className="crm-muted">{hint}</p> : null}
      {error ? <p className="crm-error">{error}</p> : null}
      {products.length === 0 && !error ? <p className="crm-muted">No products in the catalogue yet.</p> : null}
      {products.length > 0 && !disabled ? (
        <Stack gap={2}>
          <Input
            label="Search products"
            tooltip={FIELD_HINTS.product_search}
            value={query}
            placeholder="Type a name or SKU"
            autoComplete="off"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addPending();
              }
            }}
          />
          <Cluster gap={3} align="end">
            <select
              className="crm-select crm-product-picker__select"
              aria-label="Choose a product"
              title={FIELD_HINTS.product_choose}
              value={pendingId}
              onChange={(event) => setPendingId(event.target.value)}
            >
              <option value="">{available.length ? "Choose a product" : "No matching products"}</option>
              {available.map((product) => (
                <option key={product.id} value={product.id}>
                  {productLabel(product, money)}
                </option>
              ))}
            </select>
            <Input
              type="number"
              label="Qty"
              value={String(pendingQty)}
              onChange={(event) => setPendingQty(Number(event.target.value) || 1)}
            />
            <Button type="button" onClick={addPending} disabled={!pendingId && available.length !== 1}>
              Add product
            </Button>
          </Cluster>
        </Stack>
      ) : null}
      {lines.length > 0 ? (
        <ul className="crm-product-picker__list" aria-label="Added products">
          {lines.map((line) => {
            const product = products.find((item) => item.id === line.product_id);
            return (
              <li key={line.product_id} className="crm-product-picker__item">
                <Cluster gap={2} align="center" justify="between">
                  <span>{product ? productLabel(product, money) : "Added product"}</span>
                  {product ? (
                    <Link to={crmHref(`/products?q=${encodeURIComponent(product.sku || product.name)}`)}>
                      View product
                    </Link>
                  ) : null}
                  {applied?.[line.product_id]?.agreement_id || applied?.[line.product_id]?.line_code ? (
                    <span className="crm-muted">
                      Agreement{" "}
                      {applied[line.product_id].agreement_id ? (
                        <Link
                          to={crmHref(`/agreements/${applied[line.product_id].agreement_id}`)}
                          title={`Applied from ${applied[line.product_id].agreement_number ?? "agreement"}`}
                        >
                          {applied[line.product_id].agreement_number ?? applied[line.product_id].line_code}
                        </Link>
                      ) : (
                        applied[line.product_id].line_code
                      )}
                    </span>
                  ) : null}
                  {!disabled ? (
                    <Input
                      type="number"
                      label="Qty"
                      value={String(line.quantity || 1)}
                      onChange={(event) => setQuantity(line.product_id, Number(event.target.value) || 1)}
                    />
                  ) : (
                    <span className="crm-badge">Qty {line.quantity || 1}</span>
                  )}
                  {!disabled ? (
                    <Button type="button" variant="ghost" size="sm" onClick={() => remove(line.product_id)}>
                      Remove
                    </Button>
                  ) : null}
                </Cluster>
              </li>
            );
          })}
        </ul>
      ) : disabled ? (
        <p className="crm-muted">No products attached.</p>
      ) : null}
      {selectedTotal ? (
        <p className="crm-help" aria-label="Products total">
          Amount {money(selectedTotal.total, selectedTotal.currency)}
        </p>
      ) : null}
    </fieldset>
  );
}
